import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { FragranceService } from '~/service/service.fragrance';
import { UIService } from '~/service/service.ui';
import { FragranceModerationDto } from '~/shared/dto/admin/fragrance.dto';
import { FragranceModerationStatus } from '~/shared/dto/model.enums';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldEnum } from '../fields/field.enum';
import { FieldText } from '../fields/field.text';
import { Col, Row, Text } from '../view.box';

export const FragranceModerationDialog = observer<{}>(() => {
	const [ui, fragrances] = useService(UIService, FragranceService);

	const { moderation } = fragrances;
	const { open } = moderation.use();

	const form = Form.use(FragranceModerationDto, {
		name: 'FragranceModerationDto',
		onSubmit: moderation.save,
		initial: {} as FragranceModerationDto,
	});

	useEffect(() => {
		if (open) moderation.getOne().then(f => form.apply(f || {}));
		else {
			form.reset();
			moderation.reset();
		}
	}, [form, moderation, open]);

	return (
		<Dialog open={open} fullWidth maxWidth="md" onClose={ui.hideDialog}>
			<Form.Provider value={form}>
				<DialogTitle>Fragrance moderation</DialogTitle>
				<DialogContent>
					<Row gap={4} alignItems="flex-start">
						<Col gap={2} flex={1}>
							<FieldEnum
								field={form.$status}
								enumeration={FragranceModerationStatus}
								enumName="FragranceModerationStatus"
							/>
							<FieldText multiline minRows={4} field={form.$description} />
						</Col>
						<Col gap={2} width={250}>
							<Col gap={2} flex={1} justifyContent="flex-start">
								<ModerationInfoField label="Created by" value={moderation.data?.createdBy?.name} />
								<ModerationInfoField
									label="Created at"
									value={moderation.data?.createdAt}
									type="date"
								/>

								<ModerationInfoField label="Updated by" value={moderation.data?.updatedBy?.name} />
								<ModerationInfoField
									label="Updated at"
									value={moderation.data?.updatedAt}
									type="date"
								/>
							</Col>
						</Col>
					</Row>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" color="success" onClick={form.submit}>
						Save
					</Button>
					<Button variant="outlined" color="primary" onClick={ui.hideDialog}>
						Cancel
					</Button>
				</DialogActions>
			</Form.Provider>
		</Dialog>
	);
});

const ModerationInfoField: FC<{ label: string; value?: string | Date; type?: 'text' | 'date' }> = ({
	label,
	value,
	type = 'text',
}) => {
	if (type === 'date' && value) value = dayjs(value).format('LLL');

	return (
		<Col borderBottom="1px solid" borderColor="divider" gap={1}>
			<Text variant="caption" color="text.secondary">
				{label}
			</Text>
			<Text variant="body2">{value?.toString() || '-'}</Text>
		</Col>
	);
};
